import axios from 'axios';

const checkItemHasMath = (stimulus, options) => {
  const optionsString = JSON.stringify(options);
  return stimulus.indexOf('<math') >= 0 || optionsString.indexOf('<math') >= 0;
};

const mapCategoriesToItems = (scoreState) => {
  const { itemScoringReference, scoreState: scoreSections } = scoreState;

  // Map Items into Learnosity Item Objects
  return scoreSections.map((section, index) => ({
    status: 'published',
    reference: `${itemScoringReference}_category_${index}`,
    metadata: {
      scoring_type: 'per-question',
    },
    definition: {
      widgets: [{ reference: `${itemScoringReference}_rating_${index}` }],
    },
    questions: [{ reference: `${itemScoringReference}_rating_${index}` }],
  }));
};

export const convertHTMLToProsemirrorJSON = (html) => {
  const url = `${FYM_INFRA_BACKEND_PROSEMIRROR_URL}/`;
  const body = { json: html };

  // TODO: Add this back in FY-7282. Might need to be fixed
  return axios.post(url, body).then((response) => {
    // when loading already created SGs
    if (response && response.data.content) {
      return response.data;
    }

    if (!response || !response.data || !response.data.json) {
      return '';
    }

    // These line replaces malformed elements on the received JSON
    // Regular expressions are used so we can replace all instead of the first match only
    const newData = response.data.json
      .replace(/"marks":\[\{"type":"strong"\}\]/g, '"marks":["strong"]')
      .replace(/"marks":\[\{"type":"em"\}\]/g, '"marks":["em"]')
      .replace(/"marks":\[\{"type":"sup"\}\]/g, '"marks":["sup"]')
      .replace(/"marks":\[\{"type":"sub"\}\]/g, '"marks":["sub"]')
      .replace(/"marks":\[\{"type":"sub"\}\]/g, '"marks":["sub"]')
      .replace(/"styles":\[\{/g, '"marks":[{')
      .replace(/{"type":"strong"}/g, '{"_":"strong"}')
      .replace(/{"type":"em"}/g, '{"_":"em"}')
      .replace(/{"type":"sup"}/g, '{"_":"sup"}')
      .replace(/{"type":"sub"}/g, '{"_":"sub"}')
      .replace(/{"type":"underline"}/g, '{"_":"u"}')
      .replace(
        /"marks":\[\{"type":"sub"\},\{"type":"em"\}\]/g,
        '"marks":["sub","em"]',
      )
      .replace(
        /"marks":\[\{"type":"em"\},\{"type":"sub"\}\]/g,
        '"marks":["em","sub"]',
      )
      .replace(
        /"marks":\[\{"type":"sup"\},\{"type":"sub"\}\]/g,
        '"marks":["sup","sub"]',
      )
      .replace(
        /"marks":\[\{"type":"em"\},\{"type":"strong"\}\]/g,
        '"marks":["em","strong"]',
      );

    return JSON.parse(newData);
  });
};

export const convertProsemirrorJSONToHTML = (content) => {
  const url = `${FYM_INFRA_BACKEND_PROSEMIRROR_URL}/prose/html/`;

  return axios
    .post(url, content)
    .then((response) => (response && response.data) || {});
};

export const mapSectionsToQuestions = (scoreState) => {
  const {
    itemReference,
    itemScoringReference,
    scoreState: scoreSections,
  } = scoreState;

  if (scoreSections) {
    const questionsInHTML = scoreSections.map((scoringSection) => {
      const stimulusPromise = convertProsemirrorJSONToHTML(
        scoringSection.scoringInstructions,
      );

      const optionsPromise = scoringSection.scoringFeedback.map((item) =>
        Promise.all([
          convertProsemirrorJSONToHTML(item.feedback || ''),
          convertProsemirrorJSONToHTML(item.additional_feedback || ''),
          item.criterias &&
            item.criterias.length &&
            Promise.all(
              item.criterias.map((criteria) =>
                convertProsemirrorJSONToHTML(criteria.description || ''),
              ),
            ).then((criterias) =>
              criterias.map((criteria) => ({ description: criteria })),
            ),
        ]),
      );

      const optionsPromiseArray = Promise.all(optionsPromise).then((options) =>
        options.map((option, index) => ({
          label: `${index}${index === 1 ? ' point' : ' points'}`,
          value: index,
          max_score: options.length - 1,
          description: option[0] || '',
          criterias: option[2] || '',
          additional_feedback: option[1] || '',
        })),
      );

      return Promise.all([stimulusPromise, optionsPromiseArray]).then(
        (results) => ({
          title: scoringSection.sectionTitle,
          stimulus: results[0],
          options: results[1],
        }),
      );
    });

    return Promise.all(questionsInHTML)
      .then((questions) =>
        questions.map(({ title, stimulus, options }, index) => ({
          type: 'custom',
          reference: `${itemScoringReference}_rating_${index}`,
          data: {
            type: 'custom',
            valid_response: {},
            is_math: checkItemHasMath(stimulus, options),
            js: {
              question: `${LEARNOSITY_SCRIPTS_URL}/custom_cb_rubric_v0.5.js`,
              scorer: `${LEARNOSITY_SCRIPTS_URL}/custom_cb_rubric_scorer_v0.5.js`,
            },
            css: `${LEARNOSITY_SCRIPTS_URL}/custom_cb_rubric_v0.5.css`,
            editor_layout: `${LEARNOSITY_SCRIPTS_URL}/custom_cb_rubric_v0.5.html`,
            title,
            stimulus,
            options,
          },
        })),
      )
      .then((questions) => ({
        itemReference,
        categoryItems: JSON.stringify({
          items: mapCategoriesToItems(scoreState),
        }),
        categoryQuestions: JSON.stringify({
          questions,
        }),
      }));
  }
};
